<script setup lang="ts">
import { ref, watch } from 'vue'

import type { PreviewImageProps } from './types'

import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

// Fullscreen image lightbox with rotate / zoom / close. PORT-AND-SHARPEN of OrphexPreviewImage.vue
// (the analysis §2.3/§7.1.5 notes the contract mis-labelled it a "thumb"; it is in fact a lightbox
// modal, ported as such). Sharpened: dropped the `z-2000` arbitrary utility for the
// central FloatingLayer stack, dropped the `o-data-btn` dependency for plain buttons + SVG icons,
// labels are caller-supplied props (no hardcoded copy), and a broken `src` degrades to an inline
// error thumb instead of a blank frame (contract 02 §14).
withDefaults(defineProps<PreviewImageProps>(), {
  src: '',
  alt: '',
  rotateLeftLabel: 'ui.media.rotateLeft',
  rotateRightLabel: 'ui.media.rotateRight',
  zoomInLabel: 'ui.media.zoomIn',
  zoomOutLabel: 'ui.media.zoomOut',
  closeLabel: 'ui.media.close',
})

const open = defineModel<boolean>({ default: false })
const rotate = ref(0)
const scale = ref(1)
const failed = ref(false)

// Reset the transform + error state each time the lightbox opens (legacy left them stale).
watch(open, (isOpen) => {
  if (isOpen) {
    rotate.value = 0
    scale.value = 1
    failed.value = false
  }
})

function close(): void {
  open.value = false
}

function rotateBy(delta: number): void {
  rotate.value += delta
}

function zoomBy(delta: number): void {
  scale.value = Math.max(0.2, Math.round((scale.value + delta) * 10) / 10)
}
</script>

<template>
  <OverlayPortal>
    <FloatingLayer
      v-if="open"
      kind="media"
      class="ui-lightbox fixed inset-0 flex items-center justify-center p-10 overflow-auto bg-media-backdrop"
      @click="close"
    >
      <div class="ui-lightbox__toolbar fixed top-6 end-6 flex gap-3" @click.stop>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="rotateLeftLabel"
          @click="rotateBy(-90)"
        >
          <Icon name="rotate-left" :size="20" />
        </button>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="rotateRightLabel"
          @click="rotateBy(90)"
        >
          <Icon name="arrow-rotate-right" :size="20" />
        </button>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="zoomOutLabel"
          @click="zoomBy(-0.2)"
        >
          <Icon name="magnifying-glass-minus" :size="20" />
        </button>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="zoomInLabel"
          @click="zoomBy(0.2)"
        >
          <Icon name="magnifying-glass-plus" :size="20" />
        </button>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="closeLabel"
          @click="close"
        >
          <Icon name="x" :size="20" />
        </button>
      </div>
      <div class="ui-lightbox__stage flex items-center justify-center max-w-full h-5/6" @click.stop>
        <img
          v-if="!failed"
          class="ui-lightbox__img max-w-full max-h-full object-contain transition-[transform] duration-150 ease-[ease]"
          :src="src"
          :alt="alt"
          :style="{ transform: `rotate(${rotate}deg) scale(${scale})` }"
          @error="failed = true"
        />
        <div
          v-else
          class="ui-lightbox__broken flex h-32 w-32 items-center justify-center rounded-2xl bg-media-control text-on-media-muted"
          role="img"
          :aria-label="alt"
        >
          <Icon name="image-slash" :size="48" />
        </div>
      </div>
    </FloatingLayer>
  </OverlayPortal>
</template>
